import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { TmaisBadgesTransformer } from "@/features/tmaisBadges/services/tmaisBadgesTransformer";
import { tmaisBadgesColumns } from "@/features/tmaisBadges/constants/columns.constant";
import { useTmaisBadges } from "@/features/tmaisBadges/hooks/useTmaisBadges";
import {
  TmaisBadge,
  TmaisBadgeTableItem,
} from "@/features/tmaisBadges/types/tmaisBadge.type";
import { SearchIcon, Tag } from "lucide-react";
import { Link } from "react-router-dom";
import useDebounce from "@/common/hooks/useDebounce";

const TmaisBadgesList = () => {
  const { filterParams, setFilterParams } = useFilterParams();
  const [search, setSearch] = useDebounce(filterParams.search || '');
  const {
    data: badges = [],
    meta,
    isPending,
    error: badgesError = {} as any,
    isFetching,
    refetch,
  } = useTmaisBadges<TmaisBadgeTableItem[]>({
    filters: {
      search: search,
      page: filterParams.page || 1,
    },
    mapper: Mapper.mapWithTransformer<TmaisBadge[], TmaisBadgeTableItem[]>({
      transformer: TmaisBadgesTransformer.tableTransformer(),
    }),
  });

  const errorStatus = badgesError?.status ?? badgesError?.response?.status;
  const isNotFoundError = errorStatus === 404;
  const disableCreateButton = isPending || errorStatus === 403;
  const createLink = !disableCreateButton ? "/tmais-badges/new" : "";
  const createButton = (
    <Button
      name="create_tmais_badge"
      className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
      disabled={disableCreateButton}
      operationKlassToCheckPermission="Admin::Tmais::Badges::Create"
    >
      Novo destaque
    </Button>
  );

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<Tag className="size-10" strokeWidth={1.5} />}
        title="Destaques para itens"
        disableCreateButton={disableCreateButton}
        operationKlassToCheckPermission="Admin::Tmais::Badges::Create"
        actions={
          createLink ? (
            <Link to={createLink}>
              {createButton}
            </Link>
          ) : (
            createButton
          )
        }
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={isNotFoundError ? undefined : errorStatus}
        refetchFunction={refetch}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value;
                setSearch(value);
                setFilterParams((prev) => ({ ...prev, search: value, page: 1 }));
              }}
              value={filterParams.search || ''}
            />
          </div>

          <Table
            columns={tmaisBadgesColumns}
            rows={isNotFoundError ? [] : badges || []}
            isLoadingData={isPending || isFetching}
            page={filterParams.page || 1}
            totalPages={meta?.total_pages || 1}
            totalCount={isNotFoundError ? 0 : meta?.total || 0}
            hasPagination
            setPage={(page) =>
              setFilterParams((prev: any) => ({ ...prev, page }))
            }
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default TmaisBadgesList;
